/**
 * Email Validation Utilities
 * 이메일 유효성 검사 관련 유틸리티 함수
 */

import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';

/**
 * 이메일 형식 검증 정규표현식
 * - 기본 이메일 형식 체크
 * - 특수문자 및 한글 도메인 지원
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * 더 엄격한 이메일 검증 정규표현식 (RFC 5322 기반)
 */
const STRICT_EMAIL_REGEX = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;

/**
 * 번역 함수 가져오기
 */
const getTranslation = () => {
    const languageStore = useLanguageStore();
    const { createSyncT } = useTranslation();
    return createSyncT(languageStore.currentLanguage);
};

/**
 * 이메일 유효성 검사 결과 인터페이스
 */
export interface EmailValidationResult {
    isValid: boolean;
    message?: string;
}

/**
 * 이메일 유효성 검사 옵션
 */
export interface EmailValidationOptions {
    /** 엄격한 검증 모드 사용 여부 */
    strict?: boolean;
    /** 빈 값 허용 여부 */
    allowEmpty?: boolean;
    /** 최소 길이 */
    minLength?: number;
    /** 최대 길이 */
    maxLength?: number;
    /** 차단된 도메인 목록 */
    blockedDomains?: string[];
    /** 허용된 도메인 목록 (설정시 이 도메인만 허용) */
    allowedDomains?: string[];
    /** 번역 함수 (선택적, 없으면 기본 영어 메시지 사용) */
    t?: (key: string) => string;
}

/**
 * 기본 이메일 유효성 검사
 * @param email 검사할 이메일 주소
 * @returns 유효한 이메일 형식인지 여부
 */
export function isValidEmail(email: string): boolean {
    return EMAIL_REGEX.test(email);
}

/**
 * 고급 이메일 유효성 검사
 * @param email 검사할 이메일 주소
 * @param options 검증 옵션
 * @returns 검증 결과 및 메시지
 */
export function validateEmail(
    email: string,
    options: EmailValidationOptions = {}
): EmailValidationResult {
    const {
        strict = false,
        allowEmpty = false,
        minLength = 5,
        maxLength = 254, // RFC 5321 표준
        blockedDomains = [],
        allowedDomains = [],
        t
    } = options;

    // 번역 함수 가져오기 (옵션에 없으면 기본 함수 사용)
    const translate = t || getTranslation();

    // 빈 값 체크
    if (!email || email.trim().length === 0) {
        if (allowEmpty) {
            return { isValid: true };
        }
        return {
            isValid: false,
            message: translate('auth.emailRequired') || 'Please enter your email address.'
        };
    }

    const trimmedEmail = email.trim().toLowerCase();

    // 길이 체크
    if (trimmedEmail.length < minLength) {
        const defaultMessage = `Email address must be at least ${minLength} characters long.`;
        const translatedMessage = translate('auth.emailMinLength') || defaultMessage;
        // {min} 변수 치환
        const message = translatedMessage.replace('{min}', minLength.toString());
        return {
            isValid: false,
            message
        };
    }

    if (trimmedEmail.length > maxLength) {
        const defaultMessage = `Email address cannot exceed ${maxLength} characters.`;
        const translatedMessage = translate('auth.emailMaxLength') || defaultMessage;
        // {max} 변수 치환
        const message = translatedMessage.replace('{max}', maxLength.toString());
        return {
            isValid: false,
            message
        };
    }

    // 기본 형식 체크
    const regex = strict ? STRICT_EMAIL_REGEX : EMAIL_REGEX;
    if (!regex.test(trimmedEmail)) {
        return {
            isValid: false,
            message: translate('auth.emailInvalidFormat') || 'Invalid email format. (e.g., user@example.com)'
        };
    }

    // @ 기호 개수 체크
    const atCount = (trimmedEmail.match(/@/g) || []).length;
    if (atCount !== 1) {
        return {
            isValid: false,
            message: translate('auth.emailSingleAtSymbol') || 'Email address must contain exactly one @ symbol.'
        };
    }

    // 이메일 파트 분리
    const [localPart, domainPart] = trimmedEmail.split('@');

    // 로컬 파트 검증
    if (localPart.length === 0 || localPart.length > 64) {
        return {
            isValid: false,
            message: translate('auth.emailInvalidLocalPart') || 'The part before @ in the email address is invalid.'
        };
    }

    // 연속된 점(.) 체크
    if (localPart.includes('..') || domainPart.includes('..')) {
        return {
            isValid: false,
            message: translate('auth.emailConsecutiveDots') || 'Consecutive dots (..) are not allowed in email address.'
        };
    }

    // 시작/끝 점(.) 체크
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
        return {
            isValid: false,
            message: translate('auth.emailLocalPartDotStartEnd') || 'The part before @ cannot start or end with a dot (.).'
        };
    }

    // 도메인 파트 검증
    if (domainPart.length === 0 || domainPart.length > 253) {
        return {
            isValid: false,
            message: translate('auth.emailInvalidDomain') || 'Email domain is invalid.'
        };
    }

    // 도메인 TLD 체크
    const domainParts = domainPart.split('.');
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) {
        return {
            isValid: false,
            message: translate('auth.emailInvalidTld') || 'Invalid domain extension.'
        };
    }

    // 차단된 도메인 체크
    if (blockedDomains.length > 0) {
        const isBlocked = blockedDomains.some(blocked =>
            domainPart === blocked || domainPart.endsWith(`.${blocked}`)
        );
        if (isBlocked) {
            return {
                isValid: false,
                message: translate('auth.emailBlockedDomain') || 'This email domain is not allowed.'
            };
        }
    }

    // 허용된 도메인 체크
    if (allowedDomains.length > 0) {
        const isAllowed = allowedDomains.some(allowed =>
            domainPart === allowed || domainPart.endsWith(`.${allowed}`)
        );
        if (!isAllowed) {
            return {
                isValid: false,
                message: translate('auth.emailAllowedDomainsOnly') || `Only emails from allowed domains (${allowedDomains.join(', ')}) are allowed.`
            };
        }
    }

    return {
        isValid: true,
        message: translate('auth.emailValid') || 'Valid email address.'
    };
}

/**
 * 실시간 이메일 입력 검증 (타이핑 중)
 * 사용자가 입력 중일 때 사용하는 관대한 검증
 * @param email 검사할 이메일 주소
 * @param t 번역 함수 (선택적)
 * @returns 입력 중 오류 메시지 (없으면 null)
 */
export function validateEmailWhileTyping(email: string, t?: (key: string) => string): string | null {
    if (!email) {
        return null; // 입력 중에는 빈 값 허용
    }

    // 번역 함수 가져오기 (옵션에 없으면 기본 함수 사용)
    const translate = t || getTranslation();

    const trimmedEmail = email.trim();

    // @ 기호가 2개 이상인 경우만 즉시 경고
    const atCount = (trimmedEmail.match(/@/g) || []).length;
    if (atCount > 1) {
        return translate('auth.emailSingleAtSymbol') || 'Only one @ symbol is allowed.';
    }

    // 공백 포함 체크
    if (email.includes(' ')) {
        return translate('auth.emailNoSpaces') || 'Email address cannot contain spaces.';
    }

    // 한글 포함 체크
    if (/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(email)) {
        return translate('auth.emailNoKorean') || 'Email address cannot contain Korean characters.';
    }

    // @ 기호가 있고, 도메인 부분이 있는 경우
    if (atCount === 1) {
        const [localPart, domainPart] = trimmedEmail.split('@');

        // 로컬 파트 시작/끝 특수문자 체크
        if (localPart && (localPart.startsWith('.') || localPart.endsWith('.'))) {
            return translate('auth.emailLocalPartDotStartEnd') || 'The part before @ cannot start or end with a dot (.).';
        }

        // 연속된 점 체크
        if (localPart && localPart.includes('..')) {
            return translate('auth.emailConsecutiveDots') || 'Consecutive dots (..) are not allowed.';
        }

        // 도메인이 입력되기 시작했으면 기본 체크
        if (domainPart && domainPart.length > 0) {
            if (domainPart.startsWith('.') || domainPart.startsWith('-')) {
                return translate('auth.emailDomainStartInvalid') || 'Domain cannot start with a dot (.) or hyphen (-).';
            }
            if (domainPart.includes('..')) {
                return translate('auth.emailDomainConsecutiveDots') || 'Consecutive dots (..) are not allowed in domain.';
            }
        }
    }

    return null; // 입력 중에는 관대하게 처리
}

/**
 * 이메일 자동 수정 제안
 * 일반적인 오타를 수정한 이메일 제안
 * @param email 입력된 이메일
 * @returns 수정 제안 또는 null
 */
export function suggestEmailCorrection(email: string): string | null {
    if (!email || !email.includes('@')) {
        return null;
    }

    const [localPart, domainPart] = email.toLowerCase().split('@');

    // 일반적인 도메인 오타 수정
    const commonDomains: Record<string, string> = {
        // Gmail 오타들
        'gamil.com': 'gmail.com',  // 추가: 가장 흔한 오타
        'gmial.com': 'gmail.com',
        'gmai.com': 'gmail.com',
        'gmil.com': 'gmail.com',
        'gmal.com': 'gmail.com',
        'gmail.co': 'gmail.com',
        'gmail.cm': 'gmail.com',
        'gmaill.com': 'gmail.com',
        'gnail.com': 'gmail.com',
        'gemail.com': 'gmail.com',
        'gmail.con': 'gmail.com',
        'gmail.vom': 'gmail.com',

        // Naver 오타들
        'navr.com': 'naver.com',
        'naver.co': 'naver.com',
        'naver.cm': 'naver.com',
        'nave.com': 'naver.com',
        'naverr.com': 'naver.com',
        'naveer.com': 'naver.com',

        // Daum/Kakao 오타들
        'danu.com': 'daum.net',
        'daum.com': 'daum.net',
        'daun.net': 'daum.net',
        'duam.net': 'daum.net',
        'daumn.net': 'daum.net',

        // Hotmail 오타들
        'hotmai.com': 'hotmail.com',
        'hotmial.com': 'hotmail.com',
        'hotmal.com': 'hotmail.com',
        'hotmil.com': 'hotmail.com',
        'hotmali.com': 'hotmail.com',

        // Outlook 오타들
        'outlok.com': 'outlook.com',
        'outlock.com': 'outlook.com',
        'outlook.co': 'outlook.com',
        'outlook.con': 'outlook.com',

        // Yahoo 오타들
        'yahooo.com': 'yahoo.com',
        'yaho.com': 'yahoo.com',
        'yahoo.co': 'yahoo.com',
        'yahho.com': 'yahoo.com',
        'yhaoo.com': 'yahoo.com',
    };

    if (domainPart && commonDomains[domainPart]) {
        return `${localPart}@${commonDomains[domainPart]}`;
    }

    // 직접 매칭이 안 되면 유사도 체크 (레벤슈타인 거리 간단 버전)
    if (domainPart) {
        const popularDomains = [
            'gmail.com', 'naver.com', 'daum.net', 'hotmail.com',
            'outlook.com', 'yahoo.com', 'hanmail.net', 'nate.com'
        ];

        // 간단한 유사도 체크: 1-2글자 차이만 있는 경우
        for (const correctDomain of popularDomains) {
            // 길이 차이가 2 이하인 경우만 체크
            if (Math.abs(domainPart.length - correctDomain.length) <= 2) {
                let differences = 0;
                const minLength = Math.min(domainPart.length, correctDomain.length);

                // 글자별 비교
                for (let i = 0; i < minLength; i++) {
                    if (domainPart[i] !== correctDomain[i]) {
                        differences++;
                    }
                }

                // 길이 차이도 differences에 추가
                differences += Math.abs(domainPart.length - correctDomain.length);

                // 차이가 2 이하면 제안
                if (differences <= 2 && differences > 0) {
                    return `${localPart}@${correctDomain}`;
                }
            }
        }
    }

    return null;
}